import React, { useState, useEffect, useRef, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    ClipboardDocumentListIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon as SolidCheckCircleIcon,
    EyeIcon,
    EyeSlashIcon,
    XCircleIcon,
    CheckCircleIcon as OutlineCheckCircleIcon
} from '@heroicons/react/24/solid';
import './ResetPassword.css';

// API endpoints
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const REQUEST_OTP_API_ENDPOINT = `${API_BASE_URL}/auth/reset-password-otp`;
const VERIFY_OTP_API_ENDPOINT = `${API_BASE_URL}/auth/reset-password-verification`;
const SET_NEW_PASSWORD_API_ENDPOINT = `${API_BASE_URL}/auth/new-password`;

// Helper function to parse API error messages
const parseApiErrorMessage = (responseText, defaultMessage) => {
  try {
    const data = JSON.parse(responseText);
    return data?.message || defaultMessage;
  } catch (error) {
    // If responseText is not empty and not JSON, return it. Otherwise, use the default.
    return responseText || defaultMessage;
  }
};


const PasswordStrengthIndicator = ({ criteria, password }) => {
  if (!password) return null;

  const allCriteria = [
    { key: 'length', label: 'At least 8 characters' },
    { key: 'uppercase', label: 'At least one uppercase letter (A-Z)' },
    { key: 'lowercase', label: 'At least one lowercase letter (a-z)' },
    { key: 'number', label: 'At least one number (0-9)' },
    { key: 'specialChar', label: 'At least one special character (!@#$...)' },
  ];

  const unmetCriteria = allCriteria.filter(rule => !criteria[rule.key]);

  if (unmetCriteria.length === 0) {
    return (
      <div className="password-strength-guide">
        <ul className="password-guide-list">
          <li className="criteria-item criteria-met">
            <OutlineCheckCircleIcon className="criteria-icon" />
            <span>Great! Your password is secure.</span>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className="password-strength-guide">
      <p className="password-guide-title">Remaining requirements:</p>
      <ul className="password-guide-list">
        {unmetCriteria.map(rule => (
          <li key={rule.key} className="criteria-item criteria-unmet">
            <XCircleIcon className="criteria-icon" />
            <span>{rule.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Memoized TimerDisplay component
const TimerDisplay = memo(({ timer }) => {
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <p className="otp-timer">Time remaining: {formatTimer(timer)}</p>
  );
});

// Memoized MessageDisplay component
const MessageDisplay = memo(({ message, type }) => {
  if (!message) return null;
  const Icon = type === 'success' ? SolidCheckCircleIcon : ExclamationTriangleIcon;
  const colors = type === 'success' ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700';
  const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`message-box ${colors}`} role="alert">
      <div className="message-box-icon-wrapper"><Icon className={`message-box-icon ${iconColor}`} /></div>
      <div className="message-box-content"><p className="message-box-text">{message}</p></div>
    </div>
  );
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('verify');
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isRequestingOtp, setIsRequestingOtp] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });
  const [timer, setTimer] = useState(60); // 1 minute timer
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  const otpInputRefs = useRef([]);
  const verifyButtonRef = useRef(null);
  const timerRef = useRef(null); // Ref to manage timer interval

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    if (notification.message) {
      setNotification({ message: '', type: '' });
    }
  };

  useEffect(() => {
    const initialEmail = location.state?.email;

    if (!initialEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(initialEmail)) {
      navigate('/login', {
        replace: true,
        state: {
          notification: { message: 'Please enter a valid email before requesting a password reset.', type: 'error' }
        }
      });
      return;
    }
    setEmail(initialEmail);

    const requestOtpOnMount = async () => {
      setIsRequestingOtp(true);
      setNotification({ message: '', type: '' });
      try {
        const url = new URL(REQUEST_OTP_API_ENDPOINT);
        url.searchParams.append('email', initialEmail);
        const response = await fetch(url, { method: 'POST', headers: { 'accept': '*/*' } });

        const responseText = await response.text();
        
        if (response.ok) {
          setNotification({ message: `An OTP has been sent to ${initialEmail}. Please check your inbox.`, type: 'success' });
          setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
        } else {
          const errorMessage = parseApiErrorMessage(responseText, 'Failed to request OTP. This email may not be registered.');
          setNotification({ message: errorMessage, type: 'error' });
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (error) {
        setNotification({ message: 'A network error occurred. Please try again.', type: 'error' });
      } finally {
        setIsRequestingOtp(false);
        setTimer(60); // Start timer on initial OTP request
        setIsResendDisabled(true);
      }
    };
    requestOtpOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 'reset') {
      const checks = {
        length: newPassword.length >= 8,
        uppercase: /[A-Z]/.test(newPassword),
        lowercase: /[a-z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(newPassword),
      };
      setPasswordCriteria(checks);
    }
  }, [newPassword, step]);

  // Timer effect
  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
    }
    return () => clearInterval(timerRef.current); // Cleanup on unmount
  }, [timer]);

  // Notification timeout effect
  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000); // Clear after 5 seconds
      return () => clearTimeout(timer); // Cleanup timer on unmount or re-render
    }
  }, [notification.message]);

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) {
      setNotification({ message: 'Please enter a valid 6-digit OTP.', type: 'error' });
      return;
    }
    setIsLoading(true);
    try {
      const url = new URL(VERIFY_OTP_API_ENDPOINT);
      url.searchParams.append('email', email);
      url.searchParams.append('otp', enteredOtp);
      const response = await fetch(url, { method: 'POST', headers: { 'accept': '*/*' } });
      const responseText = await response.text();

      if (response.ok) {
        setNotification({ message: 'OTP verified successfully! Please set your new password.', type: 'success' });
        setStep('reset');
      } else {
        const errorMessage = parseApiErrorMessage(responseText, 'Invalid or expired OTP. Please try again.');
        setNotification({ message: errorMessage, type: 'error' });
        setOtp(new Array(6).fill(""));
        otpInputRefs.current[0]?.focus();
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred during verification. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    const allCriteriaMet = Object.values(passwordCriteria).every(Boolean);

    if (!allCriteriaMet) {
      setNotification({ message: "Password does not meet all complexity requirements.", type: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setNotification({ message: 'Passwords do not match. Please try again.', type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const url = new URL(SET_NEW_PASSWORD_API_ENDPOINT);
      url.searchParams.append('email', email);
      url.searchParams.append('password', newPassword);
      const response = await fetch(url, { method: 'POST', headers: { 'accept': '*/*' } });
      const responseText = await response.text();

      if (response.ok) {
        navigate('/login', {
          replace: true,
          state: {
            notification: { message: 'Password has been reset successfully! Please log in.', type: 'success' }
          }
        });
      } else {
        const errorMessage = parseApiErrorMessage(responseText, 'Failed to set new password. Please try again.');
        setNotification({ message: errorMessage, type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (element, index) => {
    if (isNaN(element.value)) return;
    if (notification.message) setNotification({ message: '', type: '' });
    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
    if (element.value && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      setTimeout(() => {
        verifyButtonRef.current?.focus();
      }, 0);
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) otpInputRefs.current[index - 1]?.focus();
  };

  const handleResendOtp = async () => {
    if (isResendDisabled || isLoading) return;

    setIsLoading(true);
    setNotification({ message: '', type: '' });

    try {
      const url = new URL(REQUEST_OTP_API_ENDPOINT);
      url.searchParams.append('email', email);
      const response = await fetch(url, { method: 'POST', headers: { 'accept': '*/*' } });

      const responseText = await response.text();

      if (response.ok) {
        setNotification({ message: `A new OTP has been sent to ${email}. Please check your inbox.`, type: 'success' });
        setTimer(60); // Reset timer to 1 minute
        setIsResendDisabled(true);
        setOtp(new Array(6).fill("")); // Clear OTP inputs
        otpInputRefs.current[0]?.focus();
      } else {
        const errorMessage = parseApiErrorMessage(responseText, 'Failed to resend OTP.');
        setNotification({ message: errorMessage, type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'A network error occurred while resending OTP.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-page-container">
      <div className="reset-password-form-container">
        <div className="reset-password-logo-container">
          <div className="reset-password-logo-icon-wrapper"><ClipboardDocumentListIcon className="reset-password-logo-icon" /></div>
          <h1 className="reset-password-logo-text">ZTool</h1>
        </div>

        <MessageDisplay message={notification.message} type={notification.type} />

        {isRequestingOtp ? (
          <div className="card-header">
            <h2 className="form-heading">Requesting Reset Code...</h2>
            <p className="form-subheading">Please wait while we contact the server.</p>
          </div>
        ) : step === 'verify' ? (
          <>
            <div className="card-header">
              <h2 className="form-heading">Check Your Email</h2>
              <p className="form-subheading">We sent a 6-digit code to <strong>{email}</strong>. Please enter it below.</p>
              <TimerDisplay timer={timer} />
            </div>
            <form onSubmit={handleVerifyOtp} className="form-content" noValidate>
              <div className="otp-input-group">
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    className="otp-input"
                    maxLength="1"
                    value={data}
                    onChange={e => handleOtpChange(e.target, index)}
                    onKeyDown={e => handleOtpKeyDown(e, index)}
                    onFocus={e => e.target.select()}
                    onPaste={handleOtpPaste}
                    ref={el => otpInputRefs.current[index] = el}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              <button type="submit" className="submit-button" disabled={isLoading} ref={verifyButtonRef}>
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>
              <button
                type="button"
                className="resend-otp-button"
                onClick={handleResendOtp}
                disabled={isResendDisabled || isLoading}
              >
                Resend OTP
              </button>
            </form>
          </>
        ) : step === 'reset' && (
          <>
            <div className="card-header">
              <h2 className="form-heading">Set a New Password</h2>
              <p className="form-subheading">Create a new, secure password for your account.</p>
            </div>
            <form onSubmit={handleSetNewPassword} className="form-content" noValidate>
              <div className="form-item password-wrapper">
                <label htmlFor="new-password" className="input-label">New Password</label>
                <div className="password-input-container">
                  <input id="new-password" type={showNewPassword ? 'text' : 'password'} value={newPassword} onChange={handleInputChange(setNewPassword)} placeholder="Choose a secure password" className="form-input" autoComplete="new-password" disabled={isLoading} required/>
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="password-toggle-btn" disabled={isLoading}>
                    {showNewPassword ? <EyeSlashIcon className="password-toggle-icon" /> : <EyeIcon className="password-toggle-icon" />}
                  </button>
                </div>
                <PasswordStrengthIndicator criteria={passwordCriteria} password={newPassword} />
              </div>
              <div className="form-item password-wrapper">
                <label htmlFor="confirm-password" className="input-label">Confirm New Password</label>
                <div className="password-input-container">
                  <input id="confirm-password" type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={handleInputChange(setConfirmPassword)} placeholder="Confirm your new password" className="form-input" autoComplete="new-password" disabled={isLoading} required/>
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="password-toggle-btn" disabled={isLoading}>
                    {showConfirmPassword ? <EyeSlashIcon className="password-toggle-icon" /> : <EyeIcon className="password-toggle-icon" />}
                  </button>
                </div>
              </div>
              <button type="submit" className="submit-button" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}

        <div className="tab-switch-prompt">
          <p>Remembered your password?{' '}
            <Link to="/login" className="tab-switch-link">Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;